import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { EquityDividendFormData } from '@/types/equityDividend.ts';
import equityDividendService from "@equitySecurityDividendService";

/**
 * Mutation hook for recording a new equity dividend payment. The record_equity_dividend RPC credits
 * the target account and records the dividend in one database transaction.
 */
export default function useCreateEquityDividend() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      formData,
    }: {
      formData: EquityDividendFormData;
    }): Promise<{ dividend_id: string; transaction_id: string }> => {
      return await equityDividendService.createEquityDividend({
            amount: formData.amount,
            currency: formData.currency,
            equitySecurityId: formData.equity_security_id,
            notes: formData.notes,
            paymentDate: formData.payment_date,
            targetAccountId: formData.target_account_id,
            userId: user.id,
          }
      );
    },

    onSuccess: (_result, { formData }) => {
      qc.invalidateQueries({ queryKey: ['equity-dividends', formData.equity_security_id] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      toast.success('Dividend recorded successfully.');
    },

    onError: (error: Error) => {
      if (/exchange rate/i.test(error.message)) {
        toast.error('Exchange rate unavailable. Please try again later.');
      }
      // Other errors are shown inline in mutationFn above.
    },
  });
}
